import { C } from '../../lib/landingColors'
import { PREVAILING_LANGUAGE_NOTICE_LABEL } from '../../content/legalCopy'
import {
  MarketingHeading,
  MarketingSubheading,
  MarketingParagraph,
  MarketingList,
  MarketingListItem,
  MarketingLink,
} from '../../components/MarketingShell'

// The German Privacy Policy. The English version at /privacy is the reference
// version — section 15 of both says so, and the notice above section 1 repeats
// it. A privacy policy is a notice rather than a contract, so the German text
// is a transparency aid, not a second set of promises.
//
// The translation is deliberately literal. Where the English is ambiguous, or
// disagrees with another section, the German reproduces it rather than tidying
// it up: a contradiction that exists in one language only is worse than one
// that exists in both. Those contradictions are listed in
// agentic-testing-harness/runs/p4-fix-sweep/W14-legal-de.md — they are defects
// in the ENGLISH text and fixing them fixes both.
//
// Swiss German orthography: no Eszett anywhere, "ss" throughout.

/**
 * The German Privacy Policy — a convenience translation of the reference text.
 *
 * Structurally identical to `PrivacyBodyEn`: same fourteen numbered sections
 * plus section 15, same six unnumbered subheadings under section 2, same list
 * items, same cross-references. `__tests__/legalTranslationParity.test.tsx`
 * fails the build when that stops being true.
 *
 * Section 7 is a SUMMARY of the processors — /de/legal/subprocessors is the
 * authoritative list, and it has to be right first.
 */
export function PrivacyBodyDe() {
  return (
    <>
      <aside
        role="note"
        aria-label={PREVAILING_LANGUAGE_NOTICE_LABEL}
        className="rounded-xl p-4 mb-8 text-sm leading-relaxed"
        style={{ background: `${C.primary}0c`, border: `1px solid ${C.primary}25`, color: C.muted }}
      >
        Diese deutsche Fassung ist eine Übersetzung und dient ausschliesslich
        dem besseren Verständnis. Referenzfassung ist die englische: Bei
        Widersprüchen oder Auslegungsunterschieden zwischen den beiden Fassungen
        ist die englische Fassung massgebend. Sie finden sie unter{' '}
        <MarketingLink to="/privacy">/privacy</MarketingLink>. Siehe auch
        Ziffer 15.
      </aside>

      <section>
        <MarketingHeading>1. Verantwortliche Stelle</MarketingHeading>
        <MarketingParagraph>
          Verantwortliche Stelle ist die Studioinhaberin oder der Studioinhaber,
          die oder der über die StudioFlow-Plattform tätig ist. Für Anfragen zum
          Datenschutz wenden Sie sich bitte an die Administration der
          Organisation über die in den Einstellungen der Organisation
          hinterlegte E-Mail-Adresse.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>2. Welche Daten wir erheben</MarketingHeading>
        <MarketingSubheading>Kontodaten</MarketingSubheading>
        <MarketingParagraph>Name, E-Mail-Adresse und Telefonnummer der Teammitglieder, welche die Plattform nutzen.</MarketingParagraph>

        <MarketingSubheading>Kundendaten</MarketingSubheading>
        <MarketingParagraph>
          Namen, E-Mail-Adressen, Telefonnummern, Projektangaben und
          Kommunikationsverlauf von Kundinnen und Kunden — vom Studioteam
          erfasst, um das eigene Geschäft zu führen.
        </MarketingParagraph>

        <MarketingSubheading>Foto- &amp; Dateidaten</MarketingSubheading>
        <MarketingParagraph>
          In Galerien hochgeladene Bilder, einschliesslich EXIF-Metadaten
          (Kameramodell, Aufnahmedatum, GPS-Koordinaten, falls vorhanden),
          Dateimetadaten (Grösse, Abmessungen, Format) sowie zugehörige Notizen
          der Administration und der Kundschaft.
        </MarketingParagraph>

        <MarketingSubheading>Daten von Galeriebesuchenden</MarketingSubheading>
        <MarketingParagraph>
          Wenn Galeriebesuchende mit öffentlichen Galerien interagieren:
          E-Mail-Adresse (falls für die Favoriten-Funktion eingegeben), ein
          Identifikations-Token der Kundschaft und die IP-Adresse (erfasst für
          die Zeitstempel bei der Annahme von Verträgen und Offerten).
        </MarketingParagraph>

        <MarketingSubheading>Technische Daten</MarketingSubheading>
        <MarketingParagraph>
          Browser-User-Agent (für die Abonnemente von Push-Benachrichtigungen),
          Gerätetyp und übliche Zugriffsprotokolle des Webservers.
        </MarketingParagraph>

        <MarketingSubheading>Nutzungsdaten</MarketingSubheading>
        <MarketingParagraph>Aktivitätsprotokolle innerhalb der Plattform zu Audit- und Sicherheitszwecken.</MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>3. Rechtsgrundlage (Art. 6 DSGVO)</MarketingHeading>
        <MarketingList>
          <MarketingListItem>
            <strong>Berechtigtes Interesse (Art. 6 Abs. 1 lit. f):</strong> Bearbeitung von
            Kundendaten zum Betrieb und zur Führung des Fotogeschäfts.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Vertragserfüllung (Art. 6 Abs. 1 lit. b):</strong> Erbringung des
            Dienstes gegenüber registrierten Teammitgliedern.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Einwilligung (Art. 6 Abs. 1 lit. a):</strong> Galeriebesuchende, die
            freiwillig ihre E-Mail-Adresse für die Favoriten-Funktion eingeben.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>4. Zwecke der Datenbearbeitung</MarketingHeading>
        <MarketingParagraph>Wir bearbeiten Personendaten zu den folgenden Zwecken:</MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Studio- und Projektverwaltung</MarketingListItem>
          <MarketingListItem>Terminplanung und Kommunikation mit der Kundschaft</MarketingListItem>
          <MarketingListItem>Erstellung von Rechnungen, Offerten und Verträgen</MarketingListItem>
          <MarketingListItem>Auslieferung von Fotogalerien und Durchsicht durch die Kundschaft</MarketingListItem>
          <MarketingListItem>Zusammenarbeit im Team und Aufgabenverwaltung</MarketingListItem>
          <MarketingListItem>Kalendersynchronisation</MarketingListItem>
          <MarketingListItem>Sicherheit der Plattform und Missbrauchsprävention</MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>5. Datenspeicherung &amp; Speicherort</MarketingHeading>
        <MarketingList>
          <MarketingListItem>
            <strong>Anwendungsdatenbank:</strong> Hetzner VPS, Deutschland (EU) — auf dem
            Übertragungsweg mit TLS verschlüsselt.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Foto- &amp; Videospeicher:</strong> Objektspeicher Backblaze B2,
            EU-Region (eu-central) — im Ruhezustand und auf dem Übertragungsweg
            verschlüsselt.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Auslieferung von Inhalten:</strong> Das Cloudflare-CDN liefert
            zwischengespeicherte Foto- und Videodaten von Edge-Standorten aus.
            Cloudflare wirkt als durchleitende Zwischenspeicherschicht; Ihre
            Inhalte werden über die üblichen CDN-Cache-TTLs hinaus nicht
            dauerhaft gespeichert.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Sicherungen:</strong> Dateisystem des Servers auf demselben Hetzner VPS
            (Deutschland, EU) — tägliche XLSX-Exporte der Anwendungsdatenbank mit
            rollender Aufbewahrung von 30 Tagen. Die Sicherungsdateien verlassen
            die EU nicht. Foto- und Video-Originaldateien sind nicht Teil der
            Anwendungssicherung; siehe die Allgemeinen Geschäftsbedingungen,
            Ziffer 5.5.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>6. Aufbewahrungsdauer</MarketingHeading>
        <MarketingList>
          <MarketingListItem>
            <strong>Aktive Daten:</strong> Werden für die Dauer des Bestehens der
            Organisation auf der Plattform aufbewahrt.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Weich gelöschte Daten:</strong> Werden bis zu 30 Tage aufbewahrt, um
            eine Wiederherstellung zu ermöglichen, und danach endgültig entfernt.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Sicherungen:</strong> Tägliche Sicherungen werden 30 Tage aufbewahrt,
            monatliche Archive 2 Jahre.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Audit-Protokolle:</strong> Werden 12 Monate aufbewahrt.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>7. Auftragsverarbeiter (Dritte)</MarketingHeading>
        <MarketingList>
          <MarketingListItem><strong>Hetzner Online GmbH</strong> — Serverhosting (Deutschland, EU)</MarketingListItem>
          <MarketingListItem><strong>Backblaze, Inc.</strong> — Objektspeicher für Fotos und Videos (B2, EU-Region)</MarketingListItem>
          <MarketingListItem>
            <strong>Cloudflare, Inc.</strong> — Content Delivery Network (CDN) für die
            zwischengespeicherte Auslieferung von Fotos und Videos; stellt zudem
            DNS und die TLS-Terminierung am Edge bereit. Keine dauerhafte
            Speicherung von Kundeninhalten.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Google LLC</strong> — ausschliesslich Kalendersynchronisation
            (über das eigene Google-Konto der Organisationsinhaberin oder des
            Organisationsinhabers; es werden keine Sicherungsdaten an Google übermittelt)
          </MarketingListItem>
          <MarketingListItem>
            <strong>Web-Push-Dienste</strong> — Zustellung von Push-Benachrichtigungen im
            Browser (über die Endpunkte der Browser-Anbieter)
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>8. Ihre Rechte</MarketingHeading>
        <MarketingParagraph>
          Nach der EU-Datenschutz-Grundverordnung (DSGVO) und dem Schweizer
          Bundesgesetz über den Datenschutz (nDSG / DSG) stehen Ihnen die
          folgenden Rechte zu:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem><strong>Auskunftsrecht (Art. 15 DSGVO):</strong> Eine Kopie Ihrer Personendaten verlangen.</MarketingListItem>
          <MarketingListItem><strong>Recht auf Berichtigung (Art. 16):</strong> Die Berichtigung unrichtiger Daten verlangen.</MarketingListItem>
          <MarketingListItem>
            <strong>Recht auf Löschung (Art. 17):</strong> Die Löschung Ihrer Daten
            verlangen. Inhaberinnen und Inhaber einer Organisation können ihre
            gesamte Organisation über die Funktion «Organisation löschen» in den
            Einstellungen löschen.
          </MarketingListItem>
          <MarketingListItem>
            <strong>Recht auf Datenübertragbarkeit (Art. 20):</strong> Ihre Daten in einem
            strukturierten Format exportieren (ein XLSX-Sicherungsexport steht zur Verfügung).
          </MarketingListItem>
          <MarketingListItem><strong>Widerspruchsrecht (Art. 21):</strong> Der Bearbeitung auf Grundlage berechtigter Interessen widersprechen.</MarketingListItem>
          <MarketingListItem><strong>Recht auf Widerruf der Einwilligung:</strong> Beruht eine Bearbeitung auf Einwilligung, können Sie diese jederzeit widerrufen.</MarketingListItem>
          <MarketingListItem>
            <strong>Beschwerderecht:</strong> Beim Eidgenössischen Datenschutz- und
            Öffentlichkeitsbeauftragten (EDÖB) in der Schweiz oder bei der
            zuständigen Aufsichtsbehörde in Ihrem EU-Mitgliedstaat.
          </MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>9. Cookies &amp; lokaler Speicher</MarketingHeading>
        <MarketingParagraph>
          StudioFlow verwendet keine Tracking-Cookies, keine Analyse-Skripte und
          keine Werbung Dritter. Den lokalen Speicher des Browsers nutzen wir
          ausschliesslich für:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Authentifizierungs-Token (JWT) zur Aufrechterhaltung Ihrer Anmeldesitzung</MarketingListItem>
          <MarketingListItem>Identifikations-Token der Kundschaft für Galerie-Favoriten</MarketingListItem>
          <MarketingListItem>Voreinstellungen der Oberfläche (Erscheinungsbild, Zustand der Seitenleiste)</MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>10. Meldung von Datenschutzverletzungen</MarketingHeading>
        <MarketingParagraph>
          Gemäss Art. 33/34 DSGVO und Art. 24 des Schweizer DSG melden wir eine
          Verletzung des Schutzes von Personendaten, die voraussichtlich zu
          einem Risiko für Ihre Rechte und Freiheiten führt, innerhalb von 72
          Stunden nach Kenntnisnahme der zuständigen Aufsichtsbehörde. Betroffene
          Personen werden ohne unangemessene Verzögerung benachrichtigt, wenn die
          Verletzung voraussichtlich zu einem hohen Risiko führt.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>11. Kinder</MarketingHeading>
        <MarketingParagraph>
          StudioFlow richtet sich nicht an Personen unter 16 Jahren. Wir erheben
          wissentlich keine Personendaten von Kindern.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>12. Schweizerisches Bundesgesetz über den Datenschutz (nDSG / DSG)</MarketingHeading>
        <MarketingParagraph>
          Da die Plattform von Zürich, Schweiz, aus betrieben wird, gilt
          zusätzlich zur DSGVO das Schweizer Bundesgesetz über den Datenschutz
          (nDSG, in Kraft seit dem 1. September 2023). Das bedeutet:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Wir führen ein Verzeichnis der Bearbeitungstätigkeiten gemäss Art. 12 nDSG.</MarketingListItem>
          <MarketingListItem>Grenzüberschreitende Datenbekanntgaben erfolgen im Einklang mit Art. 16-17 nDSG (alle Daten bleiben in der EU/Schweiz).</MarketingListItem>
          <MarketingListItem>Meldungen von Datenschutzverletzungen richten sich nach den Anforderungen von Art. 24 nDSG.</MarketingListItem>
        </MarketingList>
      </section>

      <section>
        <MarketingHeading>13. Änderungen dieser Erklärung</MarketingHeading>
        <MarketingParagraph>
          Wir können diese Datenschutzerklärung von Zeit zu Zeit anpassen. Bei
          wesentlichen Änderungen benachrichtigen wir die Inhaberin oder den
          Inhaber der Organisation mindestens 30 Tage im Voraus per E-Mail. Die
          aktualisierte Erklärung wird mit einem angepassten Datum der letzten
          Aktualisierung auf dieser Seite veröffentlicht.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>14. Kontakt</MarketingHeading>
        <MarketingParagraph>
          Für Anfragen zum Datenschutz, zur Ausübung Ihrer Rechte oder bei
          Fragen zu dieser Datenschutzerklärung wenden Sie sich bitte an die
          Administration der Organisation unter der E-Mail-Adresse, die auf der
          Einstellungsseite der Plattform aufgeführt ist.
        </MarketingParagraph>
      </section>

      <section>
        <MarketingHeading>15. Sprache</MarketingHeading>
        <MarketingParagraph>
          Diese Datenschutzerklärung wird in englischer und in deutscher Sprache
          veröffentlicht. Die englische Fassung ist die Referenzfassung; die
          deutsche Fassung dient ausschliesslich dem besseren Verständnis. Bei
          Widersprüchen, Unstimmigkeiten oder Auslegungsunterschieden zwischen
          den beiden Fassungen ist die englische Fassung massgebend. Die
          englische Fassung finden Sie unter{' '}
          <MarketingLink to="/privacy">/privacy</MarketingLink>.
        </MarketingParagraph>
      </section>

      <div className="mt-12 pt-6 text-sm" style={{ borderTop: `1px solid ${C.border}` }}>
        <MarketingLink to="/de/terms">Allgemeine Geschäftsbedingungen</MarketingLink>
      </div>
    </>
  )
}
